import './ReviewRawData.scss';
import { lazy, Suspense, useCallback, useRef, useState } from 'react';

import { CloseOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSessionStorageState } from 'ahooks';
import { Alert, Button, Dropdown, type MenuProps, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import FetchingSpinner from 'components/fetching-spinner';
import useCheckStatus from 'middleware/hcp/processeddata/useCheckStatus';
import { reviewDataOption } from 'utils/constants';

const DynamicCharting = lazy(() => import('./DataEditor'));

const ReviewRawData = ({
  night,
  unitId,
  score,
  setScore,
  testId,
  recordingId,
}: {
  night: string;
  unitId: string;
  score: string;
  setScore: (value: string) => void;
  testId?: string;
  recordingId?: string;
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childCompRef = useRef<any>();
  const { key, recording } = useParams();
  const keyParam = key ?? testId;
  const recordingParam = recording ?? recordingId;
  const [visible, setVisible] = useState(false);
  const [hash, setHash] = useSessionStorageState<string>(`hash-${recordingParam}`, {
    defaultValue: '',
  });

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    setScore(e.key);
  };

  const menuProps = {
    items: reviewDataOption,
    onClick: handleMenuClick,
  };

  const resetHash = () => {
    setHash('');
  };

  const toggleVisible = useCallback(() => setVisible((pre: boolean) => !pre), []);

  const { isFetching } = useCheckStatus(
    hash ?? '',
    recordingParam ?? '',
    keyParam ?? '',
    resetHash
  );

  const handleCancel = () => {
    if (childCompRef.current) {
      childCompRef.current.handleCancel();
    }
  };

  return (
    <div>
      {visible && (
        <Modal
          // eslint-disable-next-line sonarjs/deprecation
          destroyOnClose={true}
          onCancel={handleCancel}
          footer={null}
          confirmLoading={false}
          maskClosable={false}
          className="reviewmodal"
          closeIcon={<CloseOutlined id="review_data_close" />}
          open={visible}
          title={
            <div className="editmodaltitle">
              <div>{t('nightly.signal-label', { night })}</div>
              <Alert
                message={score === '3' ? t('editor.info-score') : t('editor.info4-score')}
                type="info"
                showIcon
                className="scoring-alert"
                icon={<InfoCircleOutlined />}
              />
            </div>
          }
          centered>
          {recordingParam && visible && (
            <Suspense fallback={<FetchingSpinner />}>
              <DynamicCharting
                onCancel={toggleVisible}
                unitId={unitId}
                recordingId={recordingParam}
                testId={keyParam}
                setHash={setHash}
                score={score}
                ref={childCompRef}
              />
            </Suspense>
          )}
        </Modal>
      )}
      <div className="review-button">
        <Button
          id="review-data"
          loading={isFetching}
          disabled={!!hash}
          size="middle"
          onClick={toggleVisible}
          type="primary"
          ghost>
          {t('nightly.review', { score })}
        </Button>
        <Dropdown menu={menuProps} trigger={['click']} disabled={!!hash}>
          <Button
            icon={<DownOutlined />}
            type="link"
            className="dropdown-button"
            style={hash ? { border: '1px solid #d9d9d9' } : {}}
          />
        </Dropdown>
      </div>
    </div>
  );
};

export default ReviewRawData;
